import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AggregatedIssue } from '@/shared/components/ui/common';
import { SPREADSHEET_VALIDATION_FAILED } from '@/shared/utils/parseSpreadsheetValidationError';
import type {
  LoteItem,
  LoteStatus,
  LoteStatusEvent,
  LoteTotais,
  ValidarInicioResult,
} from '../types';
import { fetchLoteItens, iniciarLote, uploadLote, validarInicioLote } from './actions';

interface ReguaLoteBatchPreValidation {
  issues: AggregatedIssue[];
  hasCritical: boolean;
  totalRows: number;
}

interface ReguaLoteBatchState {
  loteId: string | null;
  status: LoteStatus | null;
  totais: LoteTotais | null;
  itens: LoteItem[];
  pagination: { page: number; limit: number; total: number };
  corrigiveis: number;
  isUploading: boolean;
  uploadError: string | null;
  preValidation: ReguaLoteBatchPreValidation | null;
  validarInicio: ValidarInicioResult | null;
  validarInicioError: string | null;
  iniciando: boolean;
}

const DEFAULT_PAGE_SIZE = 20;

const initialState: ReguaLoteBatchState = {
  loteId: null,
  status: null,
  totais: null,
  itens: [],
  pagination: { page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 },
  corrigiveis: 0,
  isUploading: false,
  uploadError: null,
  preValidation: null,
  validarInicio: null,
  validarInicioError: null,
  iniciando: false,
};

const reguaLoteBatchSlice = createSlice({
  name: 'reguaLoteBatch',
  initialState,
  reducers: {
    setLoteId: (state, action: PayloadAction<string | null>) => {
      state.loteId = action.payload;
    },
    setStatusFromEvent: (state, action: PayloadAction<LoteStatusEvent>) => {
      state.status = action.payload.status;
      state.totais = action.payload.totais;
    },
    clearPreValidation: (state) => {
      state.preValidation = null;
      state.uploadError = null;
    },
    resetLoteBatch: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadLote.pending, (state) => {
        state.isUploading = true;
        state.uploadError = null;
        state.preValidation = null;
      })
      .addCase(uploadLote.fulfilled, (state, action) => {
        state.isUploading = false;
        state.loteId = action.payload.loteId;
      })
      .addCase(uploadLote.rejected, (state, action) => {
        state.isUploading = false;
        const payload = action.payload;
        if (payload?.code === SPREADSHEET_VALIDATION_FAILED) {
          state.preValidation = {
            issues: payload.issues ?? [],
            hasCritical: payload.hasCritical ?? true,
            totalRows: payload.totalRows ?? 0,
          };
          state.uploadError = null;
        } else {
          state.preValidation = null;
          state.uploadError = payload?.message ?? 'Erro ao enviar planilha';
        }
      })
      .addCase(fetchLoteItens.fulfilled, (state, action) => {
        state.itens = action.payload.items;
        state.corrigiveis = action.payload.corrigiveis;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
        };
      })
      .addCase(validarInicioLote.fulfilled, (state, action) => {
        state.validarInicio = action.payload;
        state.validarInicioError = null;
      })
      .addCase(validarInicioLote.rejected, (state, action) => {
        state.validarInicio = {
          canStart: false,
          errorCodes: [],
          aceiteSedexRequerido: false,
          custoSedex: null,
        };
        state.validarInicioError = action.payload ?? 'Erro ao validar início do lote';
      })
      .addCase(iniciarLote.pending, (state) => {
        state.iniciando = true;
        state.uploadError = null;
      })
      .addCase(iniciarLote.fulfilled, (state) => {
        state.iniciando = false;
      })
      .addCase(iniciarLote.rejected, (state, action) => {
        state.iniciando = false;
        state.uploadError = action.payload ?? 'Erro ao iniciar lote';
      });
  },
});

export const { setLoteId, setStatusFromEvent, clearPreValidation, resetLoteBatch } =
  reguaLoteBatchSlice.actions;
export const reguaLoteBatchReducer = reguaLoteBatchSlice.reducer;
